import { useQuery, type QueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import type { GeoPoint } from '@app/shared/geo';
import { api } from '../api';

/**
 * The default Google route right after a place is picked (owner 2026-10-23 13:34: „jak klikam wybrane miejsce to niech
 * od razu uzupełnia sobie domyślną trasę i kilometry”). The server asks it the very way a save does and remembers it
 * under `autoRouteId`; the save sends that id back, so the event gets these very numbers without asking Google again.
 * Km, time and tolls come only when the role sees them. A preview: nothing of it is typed (rule 7a).
 */
export type DefaultRoute = { autoRouteId: string; km?: number; minutes?: number | null; tollsGrosze?: number | null };

const KEY = ['events', 'route-default'] as const;
const pointKey = (p: GeoPoint) => [p.lat.toFixed(5), p.lng.toFixed(5)];
/**
 * One preview per point (to ~1 m) and "Unikaj płatnych" (review I3: not per start — every date typed would ask again);
 * the start the form had when it was first asked gives the traffic estimate.
 */
export const defaultRouteKey = (to: GeoPoint, avoidTolls: boolean) => [...KEY, ...pointKey(to), avoidTolls] as const;

/** A pause after the last pick before asking (review I3): a pin dragged or a few quick picks ask once. */
export const PREVIEW_DELAY_MS = 500;

function useSettled<T>(value: T, ms: number): T {
  const [settled, setSettled] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setSettled(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return settled;
}

/** The preview of this place; `checking` while waiting for the pause or the answer. */
export function useDefaultRoute(to: GeoPoint | null, avoidTolls: boolean, startsAt: string | null, enabled: boolean): { data: DefaultRoute | undefined; checking: boolean } {
  const wanted = enabled && to ? JSON.stringify(defaultRouteKey(to, avoidTolls)) : '';
  const settled = useSettled(wanted, PREVIEW_DELAY_MS);
  const start = useRef(startsAt);
  start.current = startsAt;
  const ready = wanted !== '' && settled === wanted;
  const q = useQuery({
    queryKey: to ? defaultRouteKey(to, avoidTolls) : [...KEY, 'none'],
    enabled: ready,
    retry: false,
    // the server keeps the route 6 h: no need to ask again while the form is open
    staleTime: 60 * 60 * 1000,
    queryFn: async () => (await api<{ route: DefaultRoute }>('/api/events/route-default', { method: 'POST', body: { to: to!, avoidTolls, ...(start.current ? { startsAt: start.current } : {}) } })).route,
  });
  if (wanted === '') return { data: undefined, checking: false };
  return { data: q.data, checking: !q.data && !q.isError && (!ready || q.isFetching) };
}

/** The id of the route previewed for this point and "Unikaj płatnych", for the save. */
export function findDefaultRoute(client: QueryClient, to: GeoPoint | null | undefined, avoidTolls: boolean): string | undefined {
  return to ? client.getQueryData<DefaultRoute>(defaultRouteKey(to, avoidTolls))?.autoRouteId : undefined;
}

type RoutePlace = { locationPoint: GeoPoint | null; avoidTolls?: boolean | undefined; chosenRoute?: unknown };

/**
 * What a save adds for the route (owner 2026-10-23 13:34): `autoRouteId` of the previewed route of the place — not
 * with a chosen route (that is the route), and in an edit only when the point or "Unikaj płatnych" change (only then
 * does the server ask for the route). Not a field of the event: the server only takes the route when it fits.
 */
export function autoRouteHint(client: QueryClient, place: RoutePlace, before?: Omit<RoutePlace, 'chosenRoute'>): { autoRouteId?: string } {
  if (place.chosenRoute || !place.locationPoint) return {};
  if (before) {
    const moved = !before.locationPoint || before.locationPoint.lat !== place.locationPoint.lat || before.locationPoint.lng !== place.locationPoint.lng;
    if (!moved && (before.avoidTolls ?? false) === (place.avoidTolls ?? false)) return {};
  }
  const id = findDefaultRoute(client, place.locationPoint, place.avoidTolls ?? false);
  return id ? { autoRouteId: id } : {};
}
